import { Alert, Avatar, Skeleton } from "@hakistudio/hakiui";
import { useQueryClient } from "@tanstack/react-query";
import { GoogleCalendarCard } from "@/components/settings/GoogleCalendarCard";
import { McpCard } from "@/components/settings/McpCard";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { useTheme, type ThemePreference } from "@/lib/theme";
import { ApiError } from "@/lib/api";
import { authClient, clearAccessToken } from "@/lib/auth";
import { useProfile } from "@/lib/queries";

const THEMES: { id: ThemePreference; label: string }[] = [
  { id: "light", label: "Light" },
  { id: "dark", label: "Dark" },
  { id: "system", label: "System" },
];

export function SettingsPage() {
  const { preference, setPreference } = useTheme();
  // Proves the whole chain end to end: session cookie -> JWT -> Worker -> Neon.
  // Shared with the shell's avatar, so both read one cached row.
  const { data: profile, error: loadError } = useProfile();
  const { data: session } = authClient.useSession();
  const queryClient = useQueryClient();

  const error = loadError
    ? loadError instanceof ApiError
      ? loadError.message
      : String(loadError)
    : null;

  return (
    <div className="flex flex-col gap-8">
      <h1 className="font-display text-5xl font-semibold lg:text-6xl">Settings</h1>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">Account</h2>
        <div className="rounded-card border border-line bg-raised p-5">
          {error ? (
            <Alert variant="danger" title="Could not load your profile">{error}</Alert>
          ) : profile ? (
            <>
              <div className="flex items-center gap-4">
                <Avatar
                  src={profile.avatarUrl ?? session?.user?.image ?? undefined}
                  name={profile.displayName ?? session?.user?.email ?? "Account"}
                  size="xl"
                  radius="full"
                />
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-base font-semibold">
                    {profile.displayName ?? "—"}
                  </span>
                  {session?.user?.email ? (
                    <span className="truncate text-sm text-ink-muted">{session.user.email}</span>
                  ) : null}
                </div>
              </div>
              <dl className="mt-5 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 border-t border-line pt-4 text-sm">
                <dt className="text-ink-faint">Timezone</dt>
                <dd>{profile.timezone}</dd>
                <dt className="text-ink-faint">User ID</dt>
                <dd className="font-mono text-xs text-ink-muted">{profile.id}</dd>
              </dl>
            </>
          ) : (
            <Skeleton height="4.5rem" radius="lg" />
          )}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">Appearance</h2>
        <div className="flex gap-2">
          {THEMES.map((t) => (
            <Chip key={t.id} active={preference === t.id} onClick={() => setPreference(t.id)}>
              {t.label}
            </Chip>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">Integrations</h2>
        <GoogleCalendarCard />
        <McpCard />
      </section>

      <section className="flex flex-col items-start gap-3">
        <h2 className="text-sm font-semibold text-ink-muted">Session</h2>
        <Button
          variant="secondary"
          onClick={async () => {
            clearAccessToken();
            await authClient.signOut();
            // Notes, tasks and the profile above are all this user's rows. Without
            // this they stay in the cache and flash at whoever signs in next.
            queryClient.clear();
          }}
        >
          Sign out
        </Button>
      </section>
    </div>
  );
}
